import React, { useState } from "react";
import "../styles/reportShowModal.scss";

/**
 * ReportShowModal — Report a show/content modal
 * Figma: 1.1 Report a show (Desktop) (24654:6528) — 570px dark card
 *
 * Dark modal overlays the video player:
 *   - Header: "Report a show" title + subtitle + close (X)
 *   - Custom dropdown: "What's the issue" (Audio & Video, Subtitle, etc.)
 *   - Textarea: "Enter reason"
 *   - Footer: Cancel + Report buttons
 *
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {function} onSubmit — called with { issue, reason }
 */

const ISSUE_OPTIONS = [
  "Audio & Video",
  "Subtitle Issues",
  "Inappropriate Content",
  "Copyright Violation",
  "Playback Error",
  "Other",
];

const ReportShowModal = ({ isOpen, onClose, onSubmit }) => {
  const [issue, setIssue] = useState("Audio & Video");
  const [reason, setReason] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onSubmit?.({ issue, reason });
    setIssue("Audio & Video");
    setReason("");
    setDropdownOpen(false);
    onClose();
  };

  const handleCancel = () => {
    setIssue("Audio & Video");
    setReason("");
    setDropdownOpen(false);
    onClose();
  };

  return (
    <div className="report-overlay" onClick={handleCancel}>
      <div className="report-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="report-modal__header">
          <div className="report-modal__header-text">
            <h2 className="report-modal__title">Report a show</h2>
            <p className="report-modal__subtitle">
              Why are you reporting this content
            </p>
          </div>
          <button
            className="report-modal__close"
            onClick={handleCancel}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="#fafafa"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="report-modal__body">
          {/* Dropdown: What's the issue */}
          <div className="report-modal__field">
            <label className="report-modal__label">What's the issue</label>
            <div className="report-modal__dropdown-wrapper">
              <button
                className="report-modal__dropdown-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                type="button"
              >
                <span>{issue}</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={`report-modal__chevron ${
                    dropdownOpen ? "report-modal__chevron--open" : ""
                  }`}
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#a3a3a3"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {dropdownOpen && (
                <ul className="report-modal__dropdown-list">
                  {ISSUE_OPTIONS.map((option) => (
                    <li key={option}>
                      <button
                        className={`report-modal__dropdown-option ${
                          issue === option
                            ? "report-modal__dropdown-option--active"
                            : ""
                        }`}
                        onClick={() => {
                          setIssue(option);
                          setDropdownOpen(false);
                        }}
                        type="button"
                      >
                        {option}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Textarea: Enter reason */}
          <div className="report-modal__field">
            <label className="report-modal__label">Enter reason</label>
            <textarea
              className="report-modal__textarea"
              placeholder="Enter text area placeholder..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="report-modal__actions">
          <button
            className="report-modal__cancel-btn"
            onClick={handleCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="report-modal__report-btn"
            onClick={handleSubmit}
            type="button"
          >
            Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportShowModal;
